import api, { ApiResponse, PaginatedApiResponse } from './axiosInstance';
import type {
  EpcScheduleDiffSummary,
  EpcScheduleReviewData,
  EpcConfirmImportResponse,
  EpcReviewOverridePatchEntry,
  EpcReviewPatchResult,
} from '../features/epc/schedule/types/scheduleDiff';

/**
 * Result of an MPP import (upload or URL). A first import returns `staged: false`;
 * a re-import over an existing schedule is staged for review and returns the staged
 * import id + diff summary so the caller can open the compare page.
 */
export type EpcMppImportResult =
  | { staged: false; import: unknown }
  | { staged: true; import: { id?: string; _id?: string }; diffSummary: EpcScheduleDiffSummary };

export interface EpcProjectAttachment {
  fileName: string;
  fileKey: string;
  /** Set when the attachment belongs to a department workspace; absent = project-level. */
  orgUnitId?: string;
  uploadedAt?: string;
}

export interface ScheduleTaskMessageAttachment {
  fileName: string;
  fileKey: string;
  contentType?: string;
  size?: number;
}

/** Discriminates plain chat messages from system-posted extension cards. */
export type ScheduleTaskMessageType = 'text' | 'extension_request' | 'extension_decision';

/** Populated from ProjectType when API uses `.populate('projectTypeId', 'name category')`. */
export interface EpcProjectTypeRef {
  _id?: string;
  id?: string;
  name?: string;
  category?: 'wind' | 'solar' | 'hybrid' | 'bess';
}

export type EpcProjectTeamRole =
  | 'epc_head'
  | 'pmo'
  | 'project_head'
  | 'project_coordinator'
  | 'department_head'
  | 'manager'
  | 'site_engineer';

export interface EpcProjectOrgUnit {
  _id?: string;
  id?: string;
  name: string;
  /** Global department-master id this unit instantiates. */
  epcDepartmentMasterId?: string;
  code?: string;
  /** Discipline / WBS-style focus tags for this project department (planning; schedule auto-assign is not wired). */
  focusAreas?: string[];
  sortOrder?: number;
  inactive?: boolean;
}

export interface EpcProjectMembershipRow {
  id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  userMobile?: string;
  role: EpcProjectTeamRole;
  orgUnitId?: string;
  orgUnitName?: string;
  reportsToUserId?: string;
  inactive: boolean;
}

export interface AiProposedDepartment {
  proposedId: string; // `new:0`, `new:1`, ...
  name: string;
  focusAreas: string[];
  rationale?: string;
}

export interface AiTaskAssignment {
  taskKey: string;
  /** Either an existing org id or a `new:*` proposedId. */
  orgRef: string;
  confidence: number;
  rationale?: string;
}

export interface AiDepartmentSuggestionResult {
  newDepartments: AiProposedDepartment[];
  assignments: AiTaskAssignment[];
  modelUsed: string;
  unassigned: { taskKey: string; reason?: string }[];
}

export type EpcProjectHealth =
  | 'on_track'
  | 'at_risk'
  | 'delayed'
  | 'completed'
  | 'upcoming'
  | 'not_configured';

export interface EpcProjectProgress {
  plannedPct: number; // 0-100, expected progress today per baseline
  actualPct: number; // 0-100, weighted by duration × percentComplete
  variancePct: number; // actualPct - plannedPct (negative = behind)
  health: EpcProjectHealth;
  overdueTaskCount: number;
  baselineEndDate?: string; // ISO; original endDatePlanned at baseline cut
  expectedEndDate?: string; // ISO; current re-plan estimate
  asOf: string; // ISO timestamp
}

/** One row of GET /epc/projects/:id/schedule/revisions (PMO Overview, spec §2.5). */
export interface EpcScheduleRevisionSummary {
  version: number;
  importedAt: string;
  createdByName: string;
  taskCount: number;
  planStart: string | null;
  planFinish: string | null;
  deltaFinishDays: number;
  deltaTaskCount: number;
  isBaseline: boolean;
  isCurrent: boolean;
}

export interface EpcProject {
  /** Normalized from API `id` (mongoose toJSON) or `_id`. */
  _id: string;
  /** May appear on raw API responses before normalization. */
  id?: string;
  name: string;
  code: string;
  description?: string;
  remarks?: string;
  status: string;
  departmentId: string;
  customerId?: unknown;
  locationId?: unknown;
  locationIds?: unknown[];
  projectTypeId?: unknown | EpcProjectTypeRef | string;
  /** EPC project category (work sub-type) — must belong to the project type. */
  projectCategoryId?: unknown | string;
  dcCapacityMw?: number;
  acCapacityMw?: number;
  commissioningDate?: string;
  startDate?: string;
  endDatePlanned?: string;
  projectManager?: string;
  epcAttachments?: EpcProjectAttachment[];
  lastMppFileKey?: string;
  lastImportAt?: string;
  scheduleStats?: {
    taskCount?: number;
    maxOutlineLevel?: number;
    formatVersion?: number;
  };
  inactive?: boolean;
  progress?: EpcProjectProgress;
  createdAt?: string;
  updatedAt?: string;
}

/** Relative to axios baseURL (must include /api/v1 prefix in env, not duplicate /v1 here). */
const base = '/epc/projects';

/** Backend toJSON plugin replaces `_id` with `id`; ensure `_id` is always set for routing. */
function normalizeEpcProject(raw: unknown): EpcProject {
  const r = raw as Record<string, unknown>;
  const oid = r['_id'] ?? r['id'];
  if (oid == null || String(oid).trim() === '') {
    throw new Error('EPC project response missing id');
  }
  return { ...(r as object), _id: String(oid) } as EpcProject;
}

class EpcProjectService {
  async list(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    inactive?: string;
    projectTypeId?: string;
    projectTypeCategory?: 'wind' | 'solar' | 'hybrid' | 'bess' | 'all' | '';
    locationId?: string;
    'sort-by'?: string;
    'sort-order'?: 'asc' | 'desc';
  }): Promise<PaginatedApiResponse<EpcProject>> {
    const res = await api.get<PaginatedApiResponse<EpcProject>>(base, { params });
    const rows = res.data.data ?? [];
    return {
      ...res.data,
      data: rows.map((row) => normalizeEpcProject(row)),
    };
  }

  async get(id: string): Promise<EpcProject> {
    const res = await api.get<ApiResponse<EpcProject>>(`${base}/${id}`);
    return normalizeEpcProject(res.data.data);
  }

  async create(body: Record<string, unknown>): Promise<EpcProject> {
    const res = await api.post<ApiResponse<EpcProject>>(base, body);
    return normalizeEpcProject(res.data.data);
  }

  async update(id: string, body: Record<string, unknown>): Promise<EpcProject> {
    const res = await api.patch<ApiResponse<EpcProject>>(`${base}/${id}`, body);
    return normalizeEpcProject(res.data.data);
  }

  async getSchedule(id: string): Promise<{ snapshot: Record<string, unknown> | null; importHistory: unknown[] }> {
    const res = await api.get<ApiResponse<{ snapshot: Record<string, unknown> | null; importHistory: unknown[] }>>(
      `${base}/${id}/schedule`
    );
    return res.data.data;
  }

  /** Ready-import revision summaries with deltas vs baseline (PMO Overview). */
  async getScheduleRevisions(projectId: string): Promise<EpcScheduleRevisionSummary[]> {
    const res = await api.get<ApiResponse<{ revisions: EpcScheduleRevisionSummary[] }>>(
      `${base}/${projectId}/schedule/revisions`
    );
    return res.data.data?.revisions ?? [];
  }

  async importMpp(id: string, file: File): Promise<EpcMppImportResult> {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post<ApiResponse<EpcMppImportResult>>(`${base}/${id}/imports/mpp`, fd, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
    return res.data.data;
  }

  async getStagedDiff(projectId: string, stagedImportId: string): Promise<EpcScheduleReviewData> {
    const res = await api.get<ApiResponse<EpcScheduleReviewData>>(
      `${base}/${projectId}/imports/${stagedImportId}/diff`
    );
    return res.data.data;
  }

  async confirmStagedImport(projectId: string, stagedImportId: string): Promise<EpcConfirmImportResponse> {
    const res = await api.post<ApiResponse<EpcConfirmImportResponse>>(
      `${base}/${projectId}/imports/${stagedImportId}/confirm`,
      {}
    );
    return res.data.data;
  }

  async discardStagedImport(projectId: string, stagedImportId: string): Promise<unknown> {
    const res = await api.post<ApiResponse<unknown>>(
      `${base}/${projectId}/imports/${stagedImportId}/discard`,
      {}
    );
    return res.data.data;
  }

  async patchReviewOverrides(
    projectId: string,
    stagedImportId: string,
    overrides: EpcReviewOverridePatchEntry[]
  ): Promise<EpcReviewPatchResult> {
    const res = await api.patch<ApiResponse<EpcReviewPatchResult>>(
      `${base}/${projectId}/imports/${stagedImportId}/review`,
      { overrides }
    );
    return res.data.data;
  }

  async importMppUrl(id: string, url: string): Promise<EpcMppImportResult> {
    const res = await api.post<ApiResponse<EpcMppImportResult>>(`${base}/${id}/imports/mpp-url`, {
      url,
    });
    return res.data.data;
  }

  async copilot(id: string, message: string): Promise<string> {
    const res = await api.post<ApiResponse<{ reply: string }>>(`${base}/${id}/copilot/schedule`, { message });
    return res.data.data.reply;
  }

  async aiSuggestDepartments(
    id: string,
    body: {
      tasks: Array<{
        key: string;
        name: string;
        outlineLevel?: number;
        isSummary?: boolean;
        currentOrgId?: string;
      }>;
      existingDepartments: Array<{ id: string; name: string; focusAreas?: string[] }>;
    }
  ): Promise<AiDepartmentSuggestionResult> {
    const res = await api.post<ApiResponse<AiDepartmentSuggestionResult>>(
      `${base}/${id}/ai/department-suggestions`,
      body,
      { timeout: 120_000 }
    );
    return res.data.data;
  }

  async saveSchedule(projectId: string, canonicalPayload: Record<string, unknown>): Promise<unknown> {
    const res = await api.patch<ApiResponse<unknown>>(`${base}/${projectId}/schedule`, { canonicalPayload });
    return res.data.data;
  }

  /** EPC-only functional departments (not OMS/AMS master departments). */
  async listOrgUnits(projectId: string): Promise<EpcProjectOrgUnit[]> {
    const res = await api.get<ApiResponse<EpcProjectOrgUnit[]>>(`${base}/${projectId}/org-units`);
    return res.data.data ?? [];
  }

  async createOrgUnit(
    projectId: string,
    body: { epcDepartmentMasterId: string; code?: string; sortOrder?: number; focusAreas?: string[] }
  ): Promise<EpcProjectOrgUnit> {
    const res = await api.post<ApiResponse<EpcProjectOrgUnit>>(`${base}/${projectId}/org-units`, body);
    return res.data.data as EpcProjectOrgUnit;
  }

  async updateOrgUnit(
    projectId: string,
    orgUnitId: string,
    body: { name?: string; code?: string; sortOrder?: number; inactive?: boolean; focusAreas?: string[] }
  ): Promise<EpcProjectOrgUnit> {
    const res = await api.patch<ApiResponse<EpcProjectOrgUnit>>(`${base}/${projectId}/org-units/${orgUnitId}`, body);
    return res.data.data as EpcProjectOrgUnit;
  }

  /** Returns all active memberships for the calling user across every EPC project — one call instead of N. */
  async getMyMemberships(): Promise<
    Array<{ epcProjectId: string; role: EpcProjectTeamRole; orgUnitId?: string; orgUnitName?: string }>
  > {
    const res = await api.get<
      ApiResponse<Array<{ epcProjectId: string; role: EpcProjectTeamRole; orgUnitId?: string; orgUnitName?: string }>>
    >(`${base}/my-memberships`);
    return res.data.data ?? [];
  }

  /** Manual hierarchy for execution; no OMS task auto-assign. */
  async listMemberships(projectId: string): Promise<EpcProjectMembershipRow[]> {
    const res = await api.get<ApiResponse<EpcProjectMembershipRow[]>>(`${base}/${projectId}/memberships`);
    return res.data.data ?? [];
  }

  async createMembership(
    projectId: string,
    body: {
      userId: string;
      role: EpcProjectTeamRole;
      orgUnitId?: string;
      reportsToUserId?: string;
    }
  ): Promise<unknown> {
    const res = await api.post<ApiResponse<unknown>>(`${base}/${projectId}/memberships`, body);
    return res.data.data;
  }

  /** Manager-only shortcut: links an existing user as site_engineer reporting to the caller. */
  async managerAddSiteEngineer(projectId: string, userId: string): Promise<unknown> {
    const res = await api.post<ApiResponse<unknown>>(`${base}/${projectId}/memberships/manager-add-site-engineer`, {
      userId,
    });
    return res.data.data;
  }

  async updateMembership(
    projectId: string,
    membershipId: string,
    body: { inactive?: boolean; orgUnitId?: string | null; reportsToUserId?: string | null }
  ): Promise<unknown> {
    const res = await api.patch<ApiResponse<unknown>>(`${base}/${projectId}/memberships/${membershipId}`, body);
    return res.data.data;
  }

  async listScheduleTaskMessages(
    projectId: string,
    taskKey: string,
    params?: { limit?: number; page?: number }
  ): Promise<{
    messages: {
      id: string;
      authorId: string;
      authorName: string;
      body: string;
      messageType?: ScheduleTaskMessageType;
      extensionRequestId?: string;
      mentionedUserIds: string[];
      attachments?: ScheduleTaskMessageAttachment[];
      createdAt: string;
      updatedAt?: string;
    }[];
    total: number;
    page: number;
    limit: number;
  }> {
    const res = await api.get<
      ApiResponse<{
        messages: {
          id: string;
          authorId: string;
          authorName: string;
          body: string;
          messageType?: ScheduleTaskMessageType;
          extensionRequestId?: string;
          mentionedUserIds: string[];
          attachments?: ScheduleTaskMessageAttachment[];
          createdAt: string;
          updatedAt?: string;
        }[];
        total: number;
        page: number;
        limit: number;
      }>
    >(`${base}/${projectId}/schedule/task-messages`, {
      params: { taskKey, limit: params?.limit, page: params?.page },
    });
    return res.data.data;
  }

  async getScheduleTaskMessagesSummary(
    projectId: string,
    taskKeys: string[]
  ): Promise<Record<string, { total: number; unreadFromOthers: number }>> {
    const taskKeysParam = taskKeys.join(',');
    const res = await api.get<
      ApiResponse<Record<string, { total: number; unreadFromOthers: number }>>
    >(`${base}/${projectId}/schedule/task-messages/summary`, {
      params: { taskKeys: taskKeysParam },
    });
    return res.data.data;
  }

  async markScheduleTaskMessagesRead(projectId: string, taskKey: string): Promise<void> {
    await api.post<ApiResponse<{ ok?: boolean }>>(`${base}/${projectId}/schedule/task-messages/read`, {
      taskKey,
    });
  }

  async postScheduleTaskMessage(
    projectId: string,
    body: {
      taskKey: string;
      body: string;
      mentionedUserIds?: string[];
      attachments?: ScheduleTaskMessageAttachment[];
    }
  ): Promise<{
    id: string;
    taskKey: string;
    authorId: string;
    authorName: string;
    body: string;
    messageType?: ScheduleTaskMessageType;
    extensionRequestId?: string;
    mentionedUserIds: string[];
    attachments?: ScheduleTaskMessageAttachment[];
    createdAt: string;
    updatedAt?: string;
  }> {
    const res = await api.post<
      ApiResponse<{
        id: string;
        taskKey: string;
        authorId: string;
        authorName: string;
        body: string;
        messageType?: ScheduleTaskMessageType;
        extensionRequestId?: string;
        mentionedUserIds: string[];
        attachments?: ScheduleTaskMessageAttachment[];
        createdAt: string;
        updatedAt?: string;
      }>
    >(`${base}/${projectId}/schedule/task-messages`, body);
    return res.data.data;
  }

  async uploadScheduleTaskMessageAttachment(
    projectId: string,
    taskKey: string,
    file: File
  ): Promise<ScheduleTaskMessageAttachment> {
    const formData = new FormData();
    formData.append('taskKey', taskKey);
    formData.append('file', file);
    const res = await api.post<ApiResponse<ScheduleTaskMessageAttachment>>(
      `${base}/${projectId}/schedule/task-messages/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data.data;
  }

  async getScheduleTaskMessageAttachmentUrl(
    projectId: string,
    fileKey: string
  ): Promise<{ url: string; fileKey: string; fileName?: string }> {
    const res = await api.get<ApiResponse<{ url: string; fileKey: string; fileName?: string }>>(
      `${base}/${projectId}/schedule/task-messages/attachments/url`,
      { params: { fileKey } }
    );
    return res.data.data;
  }

  /** DM approves an SE-raised inspection hold point. */
  async approveHoldPoint(projectId: string, holdPointId: string): Promise<unknown> {
    const res = await api.post<ApiResponse<unknown>>(`${base}/${projectId}/hold-points/${holdPointId}/approve`);
    return res.data.data;
  }

  /** DM rejects an SE-raised inspection hold point; `notes` is required by the backend. */
  async rejectHoldPoint(projectId: string, holdPointId: string, notes: string): Promise<unknown> {
    const res = await api.post<ApiResponse<unknown>>(`${base}/${projectId}/hold-points/${holdPointId}/reject`, {
      notes,
    });
    return res.data.data;
  }

  async exportScheduleMppBlob(projectId: string): Promise<Blob> {
    const res = await api.get(`${base}/${projectId}/schedule/export/mpp`, { responseType: 'blob' });
    return res.data as Blob;
  }
}

export const epcProjectService = new EpcProjectService();
export default epcProjectService;

// ----------------------------------------------------------------------------
// EPC Dashboards — service helpers
// ----------------------------------------------------------------------------
import type {
  DashboardFilters,
  ProjectHeadDashboardResponse,
  DepartmentHeadDashboardResponse,
  DepartmentManagerDashboardResponse,
  PmoPortfolioResponse,
  PmoProjectDetailResponse,
  EpcRole,
} from '../features/epc/dashboards/types';

function buildDashboardParams(f?: DashboardFilters): Record<string, string> {
  const p: Record<string, string> = {};
  if (!f) return p;
  if (f.from) p.from = f.from;
  if (f.to) p.to = f.to;
  if (f.status && f.status.length) p.status = f.status.join(',');
  if (f.projectIds && f.projectIds.length) p.projectIds = f.projectIds.join(',');
  return p;
}

export async function getProjectHeadDashboard(filters?: DashboardFilters): Promise<ProjectHeadDashboardResponse> {
  const res = await api.get('/epc/dashboards/project-head', { params: buildDashboardParams(filters) });
  return res.data?.data ?? res.data;
}

export async function getPmoPortfolio(filters?: DashboardFilters): Promise<PmoPortfolioResponse> {
  const res = await api.get('/epc/dashboards/pmo-portfolio', { params: buildDashboardParams(filters) });
  return res.data?.data ?? res.data;
}

export async function getPmoProjectDetail(projectId: string): Promise<PmoProjectDetailResponse> {
  const res = await api.get(`/epc/dashboards/pmo-portfolio/${projectId}`);
  return res.data?.data ?? res.data;
}

export async function getDepartmentHeadDashboard(filters?: DashboardFilters): Promise<DepartmentHeadDashboardResponse> {
  const res = await api.get('/epc/dashboards/department-head', { params: buildDashboardParams(filters) });
  return res.data?.data ?? res.data;
}

export async function getDepartmentManagerDashboard(filters?: DashboardFilters): Promise<DepartmentManagerDashboardResponse> {
  const res = await api.get('/epc/dashboards/department-manager', { params: buildDashboardParams(filters) });
  return res.data?.data ?? res.data;
}

export async function downloadProjectHeadReport(format: 'pdf' | 'xlsx', filters?: DashboardFilters): Promise<Blob> {
  const res = await api.get(`/epc/dashboards/project-head/report.${format}`, {
    params: buildDashboardParams(filters),
    responseType: 'blob',
  });
  return res.data;
}

export async function downloadDepartmentHeadReport(filters?: DashboardFilters): Promise<Blob> {
  const res = await api.get(`/epc/dashboards/department-head/report.xlsx`, {
    params: buildDashboardParams(filters),
    responseType: 'blob',
  });
  return res.data;
}

export async function downloadDepartmentManagerReport(filters?: DashboardFilters): Promise<Blob> {
  const res = await api.get(`/epc/dashboards/department-manager/report.xlsx`, {
    params: buildDashboardParams(filters),
    responseType: 'blob',
  });
  return res.data;
}

export async function getMyEpcRoles(): Promise<EpcRole[]> {
  const res = await api.get('/epc/me/roles');
  const body = res.data?.data ?? res.data;
  return Array.isArray(body?.roles) ? body.roles : [];
}

// ---------------------------------------------------------------------------
// Per-project workspace endpoints (Phase B)
// ---------------------------------------------------------------------------
import type {
  ProjectHeadWorkspaceResponse,
  DepartmentHeadWorkspaceResponse,
  DepartmentManagerWorkspaceResponse,
  SiteEngineerWorkspaceResponse,
} from '../features/epc/project/workspace/types';

export async function getProjectHeadWorkspace(projectId: string): Promise<ProjectHeadWorkspaceResponse> {
  const res = await api.get(`/epc/projects/${projectId}/workspace/project-head`);
  return res.data?.data ?? res.data;
}
export async function getDepartmentHeadWorkspace(projectId: string): Promise<DepartmentHeadWorkspaceResponse> {
  const res = await api.get(`/epc/projects/${projectId}/workspace/department-head`);
  return res.data?.data ?? res.data;
}
export async function getDepartmentManagerWorkspace(
  projectId: string,
  orgUnitId?: string
): Promise<DepartmentManagerWorkspaceResponse> {
  const res = await api.get(
    `/epc/projects/${projectId}/workspace/department-manager`,
    orgUnitId ? { params: { orgUnitId } } : undefined
  );
  return res.data?.data ?? res.data;
}
export async function getSiteEngineerWorkspace(projectId: string): Promise<SiteEngineerWorkspaceResponse> {
  const res = await api.get(`/epc/projects/${projectId}/workspace/site-engineer`);
  return res.data?.data ?? res.data;
}
export async function downloadProjectWorkspaceReport(
  projectId: string,
  format: 'pdf' | 'xlsx',
  orgUnitId?: string
): Promise<Blob> {
  const res = await api.get(`/epc/projects/${projectId}/workspace/report.${format}`, {
    params: orgUnitId ? { orgUnitId } : undefined,
    responseType: 'blob',
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// EPC Department-Manager workspace v2 — day plans, DM tasks, engineer history,
// per-org-unit membership management.
// ---------------------------------------------------------------------------
import type {
  ReplaceDayPlanPayload,
  DayPlanActivity,
  CreateDmTaskPayload,
  CreateDmTaskResponse,
  EngineerHistoryResponse,
  AddEngineerPayload,
  TaskHistoryEntry,
} from '../features/epc/manager/types';
import type {
  DhAssignTasksPayload,
  DhAssignTasksResponse,
  DhAddManagerPayload,
  DhManagerHistoryResponse,
  DhDprResponse,
  DhWorkloadResponse,
  DhQualitySafetyData,
  CreateIncidentPayload,
  EpcIncident,
} from '../features/epc/departmentHead/types';

// These endpoints res.send raw bodies; the axios interceptor wraps them as
// { success, message, data } — payloads live at r.data.data (see axiosInstance).

export async function getDayPlanForTask(projectId: string, taskKey: string): Promise<DayPlanActivity[]> {
  const r = await api.get(`/epc/projects/${projectId}/tasks/${encodeURIComponent(taskKey)}/day-plan`);
  return r.data.data.activities;
}

export async function saveDayPlanForTask(
  projectId: string,
  taskKey: string,
  payload: ReplaceDayPlanPayload & { orgUnitId: string }
): Promise<DayPlanActivity[]> {
  const r = await api.put(`/epc/projects/${projectId}/tasks/${encodeURIComponent(taskKey)}/day-plan`, payload);
  return r.data.data.activities;
}

export async function getTodayDayPlanForOrgUnit(projectId: string, orgUnitId: string): Promise<DayPlanActivity[]> {
  const r = await api.get(`/epc/projects/${projectId}/day-plans/today`, { params: { orgUnitId } });
  return r.data.data.activities;
}

export async function getTaskDayPlanHistory(projectId: string, taskKey: string): Promise<TaskHistoryEntry[]> {
  const r = await api.get(`/epc/projects/${projectId}/tasks/${encodeURIComponent(taskKey)}/history`);
  return r.data.data.entries;
}

export async function createDmTask(projectId: string, payload: CreateDmTaskPayload): Promise<CreateDmTaskResponse> {
  const r = await api.post(`/epc/projects/${projectId}/tasks`, payload);
  return r.data.data;
}

export async function getEngineerProjectHistory(
  projectId: string,
  engineerUserId: string
): Promise<EngineerHistoryResponse> {
  const r = await api.get(`/epc/projects/${projectId}/dm/engineers/${engineerUserId}/history`);
  return r.data.data;
}

export async function addEngineerToOrgUnit(
  projectId: string,
  payload: AddEngineerPayload
): Promise<{ membershipId: string }> {
  const r = await api.post(`/epc/projects/${projectId}/dm/memberships`, payload);
  return r.data.data;
}

export async function deactivateEngineerMembership(projectId: string, membershipId: string): Promise<void> {
  await api.patch(`/epc/projects/${projectId}/dm/memberships/${membershipId}/deactivate`);
}

// ---------------------------------------------------------------------------
// EPC Daily planning v3 — quantity master, day-seed, PPC, productivity,
// hindrance register.
// ---------------------------------------------------------------------------
import type {
  DaySeed,
  Hindrance,
  PpcResponse,
  ProductivityResponse,
  TaskQuantity,
  TaskQuantityInput,
  TaskQuantityRow,
} from '../features/epc/manager/types';

/** Returns null when no quantity master exists yet for the task. */
export async function getTaskQuantity(projectId: string, taskKey: string): Promise<TaskQuantity | null> {
  const r = await api.get(`/epc/projects/${projectId}/tasks/${encodeURIComponent(taskKey)}/quantity`);
  return r.data.data.quantity ?? null;
}

export async function putTaskQuantity(
  projectId: string,
  taskKey: string,
  body: TaskQuantityInput
): Promise<TaskQuantity> {
  const r = await api.put(`/epc/projects/${projectId}/tasks/${encodeURIComponent(taskKey)}/quantity`, body);
  return r.data.data.quantity;
}

export async function listTaskQuantities(projectId: string): Promise<TaskQuantityRow[]> {
  const r = await api.get(`/epc/projects/${projectId}/quantities`);
  return r.data.data.quantities ?? [];
}

export async function deleteTaskQuantity(projectId: string, taskKey: string): Promise<void> {
  await api.delete(`/epc/projects/${projectId}/tasks/${encodeURIComponent(taskKey)}/quantity`);
}

/** Previous-day incompletes + engineer tomorrow-plans for the plan builder's "Seed this day". */
export async function getDaySeed(projectId: string, orgUnitId: string, date: string): Promise<DaySeed> {
  const r = await api.get(`/epc/projects/${projectId}/day-plans/seed`, { params: { orgUnitId, date } });
  return r.data.data;
}

export async function getPpc(
  projectId: string,
  params: { orgUnitId?: string; from: string; to: string; groupBy?: 'day' | 'week' | 'engineer' }
): Promise<PpcResponse> {
  const r = await api.get(`/epc/projects/${projectId}/ppc`, { params });
  return r.data.data;
}

export async function getProductivity(
  projectId: string,
  params?: { orgUnitId?: string; from?: string; to?: string }
): Promise<ProductivityResponse> {
  const r = await api.get(`/epc/projects/${projectId}/productivity`, { params });
  return r.data.data;
}

export async function listHindrances(
  projectId: string,
  params?: { orgUnitId?: string; status?: 'open' | 'resolved'; from?: string; to?: string }
): Promise<Hindrance[]> {
  const r = await api.get(`/epc/projects/${projectId}/hindrances`, { params });
  return r.data.data.hindrances ?? [];
}

export async function resolveHindrance(
  projectId: string,
  hindranceId: string,
  resolutionNote: string
): Promise<Hindrance> {
  const r = await api.post(`/epc/projects/${projectId}/hindrances/${hindranceId}/resolve`, { resolutionNote });
  return r.data.data.hindrance;
}

// ---------------------------------------------------------------------------
// EPC Department-Manager workspace v2 — planning focus & engineer daily logs.
// ---------------------------------------------------------------------------
import type {
  PlanningFocusResponse,
  DailyLogTimelineRow,
  DailyLogDetail,
} from '../features/epc/manager/types';

export async function getPlanningFocus(
  projectId: string,
  opts: { orgUnitId: string; date?: string }
): Promise<PlanningFocusResponse> {
  const r = await api.get(`/epc/projects/${projectId}/planning/focus`, {
    params: opts.date ? { orgUnitId: opts.orgUnitId, date: opts.date } : { orgUnitId: opts.orgUnitId },
  });
  return r.data?.data ?? r.data;
}

export async function getEngineerDailyLogTimeline(
  projectId: string,
  engineerUserId: string
): Promise<DailyLogTimelineRow[]> {
  const r = await api.get(`/epc/projects/${projectId}/engineers/${engineerUserId}/daily-logs`);
  const body = r.data?.data ?? r.data;
  return body?.rows ?? [];
}

export async function getEngineerDailyLog(
  projectId: string,
  engineerUserId: string,
  date: string
): Promise<DailyLogDetail> {
  const r = await api.get(`/epc/projects/${projectId}/engineers/${engineerUserId}/daily-logs/${date}`);
  return r.data?.data ?? r.data;
}

export async function postManagerNote(
  projectId: string,
  engineerUserId: string,
  date: string,
  text: string
): Promise<DailyLogDetail> {
  const r = await api.post(
    `/epc/projects/${projectId}/engineers/${engineerUserId}/daily-logs/${date}/manager-notes`,
    { text }
  );
  return r.data?.data ?? r.data;
}

export async function markLogReviewed(
  projectId: string,
  engineerUserId: string,
  date: string
): Promise<DailyLogDetail> {
  const r = await api.post(
    `/epc/projects/${projectId}/engineers/${engineerUserId}/daily-logs/${date}/review`,
    {}
  );
  return r.data?.data ?? r.data;
}

// --- Department Head (DH) workspace v2 ---

export async function dhAssignTasksToManager(
  projectId: string,
  payload: DhAssignTasksPayload
): Promise<DhAssignTasksResponse> {
  const r = await api.post(`/epc/projects/${projectId}/dh/tasks/assign`, payload);
  return r.data;
}

export async function dhAddManagerToOrgUnit(
  projectId: string,
  payload: DhAddManagerPayload
): Promise<{ membershipId: string }> {
  const r = await api.post(`/epc/projects/${projectId}/dh/memberships`, payload);
  return r.data;
}

export async function dhDeactivateManagerMembership(
  projectId: string,
  membershipId: string
): Promise<void> {
  await api.patch(`/epc/projects/${projectId}/dh/memberships/${membershipId}/deactivate`);
}

export async function getDhManagerHistory(
  projectId: string,
  managerUserId: string
): Promise<DhManagerHistoryResponse> {
  const r = await api.get(`/epc/projects/${projectId}/dh/managers/${managerUserId}/history`);
  return r.data?.data ?? r.data;
}

export async function getDhEngineerHistory(
  projectId: string,
  engineerUserId: string
): Promise<EngineerHistoryResponse> {
  const r = await api.get(`/epc/projects/${projectId}/dh/engineers/${engineerUserId}/history`);
  return r.data?.data ?? r.data;
}

export async function getDhDepartmentDpr(
  projectId: string,
  date: string
): Promise<DhDprResponse> {
  const r = await api.get(`/epc/projects/${projectId}/dh/dpr`, { params: { date } });
  return r.data?.data ?? r.data;
}

export async function reviewDhManagerDpr(
  projectId: string,
  date: string,
  payload: { managerUserId: string; note?: string }
): Promise<{ reviewedAt: string }> {
  const r = await api.post(`/epc/projects/${projectId}/dh/dpr/${date}/review`, payload);
  return r.data?.data ?? r.data;
}

export async function getDhDepartmentWorkload(projectId: string, from: string, to: string): Promise<DhWorkloadResponse> {
  const r = await api.get(`/epc/projects/${projectId}/dh/workload`, { params: { from, to } });
  return r.data?.data ?? r.data;
}

export async function downloadDhDprReport(projectId: string, format: 'xlsx' | 'pdf', from: string, to: string): Promise<Blob> {
  const r = await api.get(`/epc/projects/${projectId}/dh/dpr/report.${format}`, { params: { from, to }, responseType: 'blob' });
  return r.data;
}

export async function getDhQualitySafety(projectId: string): Promise<DhQualitySafetyData> {
  const r = await api.get(`/epc/projects/${projectId}/dh/quality-safety`);
  return r.data?.data ?? r.data;
}
export async function createDhIncident(projectId: string, payload: CreateIncidentPayload): Promise<EpcIncident> {
  const r = await api.post(`/epc/projects/${projectId}/dh/incidents`, payload);
  return r.data?.data ?? r.data;
}
export async function closeDhIncident(projectId: string, incidentId: string): Promise<EpcIncident> {
  const r = await api.patch(`/epc/projects/${projectId}/dh/incidents/${incidentId}/close`);
  return r.data?.data ?? r.data;
}
